'use client';

import React, { useMemo, useState } from 'react';
import { faHandHoldingCircleDollar } from '@fortawesome/pro-solid-svg-icons';
import useFilterSets from 'src/hooks/filters/useFilterSets';
import useGetOpenPaymentsSuggestionsV2 from 'src/hooks/openPayments/useGetOpenPaymentsSuggestionsV2';
import {
  FilterIcon,
  IncludeExcludeSelect,
  ConnectorSelect,
  FilterAutocomplete,
  Divider,
  FilterLabel,
  ClearFilterButton,
} from 'src/components/FilterSets/components';
import { SelectedFilters } from 'src/components/FilterSets/SelectedFilters';
import type { FilterValue } from 'src/context/reducers/filterSetReducer';

const OpenPayments = ({ location }: { location: string }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const { filterSet, updateFilter, removeFilterValue } =
    useFilterSets(location);
  const filterValue = filterSet.openPayments;

  const { data: suggestions, isLoading } = useGetOpenPaymentsSuggestionsV2({
    q: searchTerm,
  });

  const options = useMemo(() => {
    return suggestions?.results.map((result) => ({
      value: result.id,
      label: result.name,
    }));
  }, [suggestions]);

  const handleRemoveFilter = (filterLabel: string) => {
    removeFilterValue('openPayments', filterLabel);
  };

  const handleFilterChange = (value: FilterValue) => {
    updateFilter('openPayments', value);
  };

  const handleSearch = (term: string) => {
    setSearchTerm(term);
  };

  const hasValues = filterValue?.values?.length;

  return (
    <div className="w-full max-w-7xl mx-auto flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <FilterLabel>Open Payments</FilterLabel>
        <ClearFilterButton
          type="openPayments"
          isDisabled={!hasValues}
          location={location}
        />
      </div>
      <div className="self-stretch px-4 py-2 bg-white rounded-full outline outline-1 -outline-offset-1 outline-neutral-300 inline-flex justify-between items-center">
        <div className="flex-1 flex justify-start items-center gap-2">
          <FilterIcon icon={faHandHoldingCircleDollar} />
          <IncludeExcludeSelect
            title="Open Payments"
            value={filterValue}
            onChange={handleFilterChange}
          />
          <Divider />
          <ConnectorSelect
            title="Open Payments"
            value={filterValue}
            onChange={handleFilterChange}
            isAll={filterValue?.is_all}
          />
          <Divider />
          <FilterAutocomplete
            options={options || []}
            value={filterValue}
            onChange={handleFilterChange}
            isLoading={isLoading}
            placeholder="Search Open Payments..."
            onSearch={handleSearch}
          />
        </div>
      </div>
      <SelectedFilters
        filters={filterValue?.values.map((v) => v.label)}
        onRemove={handleRemoveFilter}
        isAll={filterValue?.is_all}
      />
    </div>
  );
};

export default OpenPayments;
